import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Save, ChevronLeft, ChevronRight, Loader2, Upload, X, Image as ImageIcon, Search, Plus, Check, UserCheck, ClipboardPaste, Info, Trash2, FileText } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { SubmittalPreviewModal } from '../../components/SubmittalPreviewModal';
import { FormField, FormSelect, MultiSelectCheckboxes, ReviewSection, WizardStepper, ReviewProductHeader } from '../shared/WizardComponents';
import ClipboardPasteModal from '../../components/ClipboardPasteModal';
import { useSubmittalsManager, commitSubmittals } from '../shared/useSubmittalsManager';
import { WizardSubmittalsStep, WizardSubmittalsReview } from '../shared/WizardSubmittalsStep';
import { COUNTRIES, getCountryName } from '../../utils/countries';
import { Badge } from '../../components/ui/badge';
import { ZoomControls } from '../shared/ElevationViewer';

/* ── Step definitions ─────────────────────────────────────── */
const WIZARD_STEPS = [
  { id: 1, title: 'Product Identification' },
  { id: 2, title: 'Material & Fabrication Properties' },
  { id: 3, title: 'Fire, Code & Assembly Compliance' },
  { id: 4, title: 'Warranty & Responsibility' },
  { id: 5, title: 'Submittals' },
  { id: 6, title: 'Visual Coverage' },
  { id: 7, title: 'Review Summary' },
];
const TOTAL_STEPS = WIZARD_STEPS.length;

/* ── Marker types for Visual Coverage ─────────────────────── */
const FSM_MARKER_TYPES = ['Sill', 'Head', 'Parapet'];
const FSM_MARKER_META = {
  'Sill':    { color: '#3b82f6', shape: 'rounded-sm',   hoverBg: 'bg-blue-500 hover:bg-blue-600' },
  'Head':    { color: '#ea580c', shape: 'rounded-full', hoverBg: 'bg-orange-500 hover:bg-orange-600' },
  'Parapet': { color: '#16a34a', shape: 'rounded-full', hoverBg: 'bg-green-500 hover:bg-green-600' },
};

/* ── Constants ────────────────────────────────────────────── */
const PRODUCT_ROLES = ['Sill flashing', 'Head flashing', 'Jamb flashing', 'Parapet coping', 'Drip edge', 'Reglet / termination'];
const APPLICATION_AREAS = ['Window / door perimeter', 'Curtain wall interface', 'Parapet / spandrel', 'Balcony / slab edge'];
const METAL_TYPES = ['Aluminum', 'Galvanized steel', 'Stainless steel', 'Copper'];
const FABRICATION_METHODS = ['Brake-formed', 'Shop fabricated', 'Field modified'];
const NFPA_OPTIONS = ['Yes', 'No'];
const SUBMITTAL_DOC_TYPES = ['Shop drawings', 'Fabrication details', 'Product data', 'Compatibility letters', 'Warranty documents'];

/* ── Form defaults ────────────────────────────────────────── */
const INITIAL_FORM = {
  manufacturerId: '', productNameModel: '', countryCode: '',
  productRole: [], applicationArea: [],
  imageFile: null, imagePreview: null,
  metalType: '', gaugeThickness: '', finishCoating: '', fabricationMethod: '',
  fireCodeCompliance: { nfpa285Compatibility: '', astmE84: '', assemblyReference: '', localCodeNotes: '' },
  warranty: { manufacturerWarrantyYears: '', installerWarrantyYears: '', scopeExclusions: '', maintenanceResponsibility: '' },
  visualCoverage: { elevationFileName: '', elevationUrl: '', elevationPublicId: '', markers: [], markerType: '' },
  submittals: [],
};

/* ── Sanitizers ───────────────────────────────────────────── */
const sanitizeFraction = (v) => v.replace(/[^0-9./]/g, '');
const sanitizeNumeric = (v) => v.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
const isNumericValue = (v) => !v || /^-?\d*\.?\d*$/.test(v.trim());

/* ── Validation mapping ───────────────────────────────────── */
const STEP_FIELD_PREFIXES = {
  1: ['productNameModel', 'manufacturerId', 'productRole', 'applicationArea'],
  2: ['metalType'],
  3: ['fireCodeCompliance.'],
  4: ['warranty.'],
  5: [],
  6: [],
  7: [],
};

/* ── Session storage ──────────────────────────────────────── */
const STORAGE_KEY_PREFIX = 'specard_fsm_wizard_v1';
const getStorageKey = (companyId, draftId) => `${STORAGE_KEY_PREFIX}_${companyId}_${draftId || 'new'}`;
const clearWizardState = (companyId, draftId) => {
  try {
    sessionStorage.removeItem(getStorageKey(companyId, draftId));
    if (draftId) sessionStorage.removeItem(getStorageKey(companyId, 'new'));
  } catch {}
};

/* ── Badge helper (Review) ────────────────────────────────── */
const badge = (val) => {
  if (!val) return null;
  const isPos = ['Yes', 'Pass', 'Confirmed'].includes(val);
  const isNeg = ['No', 'Fail', 'Not confirmed'].includes(val);
  if (!isPos && !isNeg) return val;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded ${isPos ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{val}</span>;
};

/* ── Merge defaults (backward compat for old products) ───── */
const mergeFormDefaults = (saved) => {
  if (!saved) return { ...INITIAL_FORM };
  const fcc = saved.fireCodeCompliance || {};
  const oldFc = saved.fireCompliance || {};
  const w = saved.warranty || {};
  return {
    ...INITIAL_FORM,
    manufacturerId: saved.manufacturerId || '',
    productNameModel: saved.productNameModel || '',
    countryCode: saved.countryCode || '',
    productRole: saved.productRole || [],
    applicationArea: saved.applicationArea || [],
    metalType: saved.metalType || '',
    gaugeThickness: saved.gaugeThickness || saved.gauge || '',
    finishCoating: saved.finishCoating || saved.materialPerformance?.coating || saved.materialPerformance?.finishType || '',
    fabricationMethod: saved.fabricationMethod || saved.fabrication?.fabricationMethod || '',
    fireCodeCompliance: {
      nfpa285Compatibility: fcc.nfpa285Compatibility || '',
      astmE84: fcc.astmE84 || oldFc.astmStandards || '',
      assemblyReference: fcc.assemblyReference || oldFc.ibcComplianceStatement || '',
      localCodeNotes: fcc.localCodeNotes || oldFc.nfpaReferences || '',
    },
    warranty: {
      manufacturerWarrantyYears: w.manufacturerWarrantyYears || '',
      installerWarrantyYears: w.installerWarrantyYears || '',
      scopeExclusions: w.scopeExclusions || w.exclusions || '',
      maintenanceResponsibility: w.maintenanceResponsibility || w.maintenanceRequirements || '',
    },
    submittals: saved.submittals || [],
    visualCoverage: { ...INITIAL_FORM.visualCoverage, ...(saved.visualCoverage || {}), markers: saved.visualCoverage?.markers || [] },
    imageFile: null, imagePreview: null,
  };
};

/* ══════════════════════════════════════════════════════════ */
export const AddFlashingWizard = ({ open, onOpenChange, onComplete, onRefresh, editProduct, companyId, projectId, csiItemId }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const [mfrSearch, setMfrSearch] = useState('');
  const [mfrDropdownOpen, setMfrDropdownOpen] = useState(false);
  const [creatingMfr, setCreatingMfr] = useState(false);
  const mfrRef = useRef(null);
  const [confirmState, setConfirmState] = useState({ open: false });
  const [previewSubmittal, setPreviewSubmittal] = useState(null);
  const initialFormRef = useRef(null);
  const imageFileRef = useRef(null);
  const isEdit = !!editProduct;
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteUploading, setPasteUploading] = useState(false);
  const [elevationUploading, setElevationUploading] = useState(false);
  const [elevationZoom, setElevationZoom] = useState(1);
  const submittals = useSubmittalsManager();
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  /* ── Fetch manufacturers ─────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/thermal-insulation-manufacturers`).then(r => setManufacturers(r.data)).catch(() => {});
  }, [open]);

  /* ── Init / edit / restore ───────────────────────────── */
  useEffect(() => {
    if (!open) return;
    if (isEdit && editProduct) {
      const editForm = mergeFormDefaults(editProduct);
      editForm.manufacturerId = editProduct.manufacturerId || '';
      if (editProduct.imageCloudinary?.urls?.medium || editProduct.imageUrl) {
        editForm.imagePreview = editProduct.imageCloudinary?.urls?.medium || editProduct.imageUrl;
      }
      setForm(editForm);
      setDraftId(editProduct.id);
      initialFormRef.current = { ...editForm, imageFile: null };
      submittals.init(editProduct.submittals || []);
      setStep(1);
    } else {
      const saved = sessionStorage.getItem(getStorageKey(companyId, draftId));
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setStep(parsed.step || 1);
          setDraftId(parsed.draftId || null);
          setForm(mergeFormDefaults(parsed.form));
        } catch { /* ignore */ }
      }
      initialFormRef.current = initialFormRef.current || { ...INITIAL_FORM };
    }
  }, [open, isEdit, editProduct]);

  /* ── Auto-save to sessionStorage (create mode only) ──── */
  useEffect(() => {
    if (!open || isEdit) return;
    const toSave = { step, draftId, form: { ...form, imageFile: null, imagePreview: form.imagePreview?.startsWith?.('blob:') ? null : form.imagePreview } };
    sessionStorage.setItem(getStorageKey(companyId, draftId), JSON.stringify(toSave));
  }, [step, form, draftId, open, companyId, isEdit]);

  /* ── Dirty state ─────────────────────────────────────── */
  const isDirty = (() => {
    if (submittals.isDirty) return true;
    if (!initialFormRef.current) return false;
    if (form.imageFile) return true;
    const a = { ...form, imageFile: null, imagePreview: null };
    const b = { ...initialFormRef.current, imageFile: null, imagePreview: null };
    if (JSON.stringify(a.visualCoverage) !== JSON.stringify(b.visualCoverage)) return true;
    return JSON.stringify(a) !== JSON.stringify(b);
  })();

  /* ── Click-outside: close dropdowns ──────────────────── */
  useEffect(() => {
    const h = (e) => {
      if (mfrRef.current && !mfrRef.current.contains(e.target)) setMfrDropdownOpen(false);
      if (countryRef.current && !countryRef.current.contains(e.target)) setCountryDropdownOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  /* ── Derived state ───────────────────────────────────── */
  const selectedManufacturer = manufacturers.find(m => m.id === form.manufacturerId);
  const filteredManufacturers = manufacturers.filter(m => m.name.toLowerCase().includes(mfrSearch.toLowerCase()));
  const showAddNew = mfrSearch.trim() && !filteredManufacturers.some(m => m.name.toLowerCase() === mfrSearch.trim().toLowerCase());
  const existingImageUrl = editProduct?.imageCloudinary?.urls?.medium || editProduct?.imageUrl || null;
  const displayImage = form.imagePreview || existingImageUrl;
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.toLowerCase().includes(countrySearch.toLowerCase()));
  const selectedCountry = COUNTRIES.find(c => c.code === form.countryCode);

  /* ── Field updaters ──────────────────────────────────── */
  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateNested = (group, field, value) => setForm(prev => ({ ...prev, [group]: { ...prev[group], [field]: value } }));

  /* ── Close / cancel ──────────────────────────────────── */
  const handleClose = () => {
    if (isDirty) {
      setConfirmState({
        open: true, title: 'Unsaved Changes',
        description: 'You have unsaved changes. Are you sure you want to close? Your changes will be lost.',
        confirmText: 'Close', variant: 'destructive',
        onConfirm: () => { doClose(); setConfirmState({ open: false }); },
      });
    } else { doClose(); }
  };

  const doClose = () => {
    if (draftId) clearWizardState(companyId, draftId);
    clearWizardState(companyId, 'new');
    setStep(1); setForm({ ...INITIAL_FORM }); setDraftId(null); setErrors({}); setMfrSearch(''); setCountrySearch(''); setElevationZoom(1);
    submittals.rollback();
    initialFormRef.current = null;
    onOpenChange(false);
  };

  /* ── Validation ──────────────────────────────────────── */
  const validateAllSteps = () => {
    const e = {};
    if (!form.manufacturerId) e.manufacturerId = 'Required';
    if (!form.productNameModel?.trim()) e.productNameModel = 'Required';
    if (!form.productRole || form.productRole.length === 0) e.productRole = 'At least one required';
    if (!form.applicationArea || form.applicationArea.length === 0) e.applicationArea = 'At least one required';
    if (!form.metalType) e.metalType = 'Required';
    if (form.warranty.manufacturerWarrantyYears && !isNumericValue(form.warranty.manufacturerWarrantyYears)) e['warranty.manufacturerWarrantyYears'] = 'Must be numeric';
    if (form.warranty.installerWarrantyYears && !isNumericValue(form.warranty.installerWarrantyYears)) e['warranty.installerWarrantyYears'] = 'Must be numeric';
    return e;
  };

  const getFirstErrorStep = (errs) => {
    for (let s = 1; s <= TOTAL_STEPS; s++) {
      const prefixes = STEP_FIELD_PREFIXES[s] || [];
      for (const key of Object.keys(errs)) {
        if (prefixes.some(p => key === p || key.startsWith(p))) return s;
      }
    }
    return 1;
  };

  /* ── Build payload ───────────────────────────────────── */
  const buildPayload = (isDraft = false) => {
    const p = {
      productNameModel: form.productNameModel?.trim() || '',
      status: isDraft ? 'draft' : 'active',
    };
    if (csiItemId) p.csiItemId = csiItemId;
    if (form.manufacturerId) p.manufacturerId = form.manufacturerId;
    if (form.countryCode) p.countryCode = form.countryCode;
    if (form.productRole?.length) p.productRole = form.productRole;
    if (form.applicationArea?.length) p.applicationArea = form.applicationArea;
    if (form.metalType) p.metalType = form.metalType;
    if (form.gaugeThickness) p.gaugeThickness = form.gaugeThickness;
    if (form.finishCoating) p.finishCoating = form.finishCoating;
    if (form.fabricationMethod) p.fabricationMethod = form.fabricationMethod;
    const fcc = form.fireCodeCompliance;
    if (fcc.nfpa285Compatibility || fcc.astmE84 || fcc.assemblyReference || fcc.localCodeNotes) {
      p.fireCodeCompliance = fcc;
    }
    const w = form.warranty;
    if (w.manufacturerWarrantyYears || w.installerWarrantyYears || w.scopeExclusions || w.maintenanceResponsibility) {
      p.warranty = w;
    }
    const vc = form.visualCoverage;
    if (vc.elevationUrl || vc.markers?.length) p.visualCoverage = vc;
    return p;
  };

  /* ── Save as Draft ───────────────────────────────────── */
  const handleSaveDraft = async () => {
    if (!form.productNameModel?.trim()) { setErrors(e => ({ ...e, productNameModel: 'Required' })); toast.error('Product Name is required'); return; }
    setSaving(true);
    try {
      const payload = buildPayload(true);
      let res;
      if (draftId) {
        res = await axios.patch(`${API}/flashing-sheet-metal-products/${projectId}/${draftId}`, payload);
      } else {
        res = await axios.post(`${API}/flashing-sheet-metal-products/${projectId}`, payload);
        setDraftId(res.data.id);
      }
      const pid = res.data.id || draftId;
      if (form.imageFile && pid) {
        const fd = new FormData(); fd.append('file', form.imageFile);
        await axios.post(`${API}/flashing-sheet-metal-products/${projectId}/${pid}/upload-image`, fd);
      }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'flashing-sheet-metal-products', axios, API);
        const fresh = await axios.get(`${API}/flashing-sheet-metal-products/${projectId}/${pid}`);
        submittals.init(fresh.data?.submittals || []);
      }
      initialFormRef.current = { ...form, imageFile: null, imagePreview: null };
      toast.success('Draft saved');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally { setSaving(false); }
  };

  /* ── Finalize (Create / Save) ────────────────────────── */
  const handleFinalize = async () => {
    const errs = validateAllSteps();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const targetStep = getFirstErrorStep(errs);
      if (targetStep !== step) setStep(targetStep);
      toast.error('Please complete all required fields before finalizing');
      return;
    }
    setErrors({});
    setFinalizing(true);
    try {
      const payload = buildPayload(false);
      let res;
      if (draftId) {
        res = await axios.patch(`${API}/flashing-sheet-metal-products/${projectId}/${draftId}`, payload);
      } else {
        res = await axios.post(`${API}/flashing-sheet-metal-products/${projectId}`, payload);
      }
      const pid = res.data.id || draftId;
      if (form.imageFile && pid) {
        const fd = new FormData(); fd.append('file', form.imageFile);
        await axios.post(`${API}/flashing-sheet-metal-products/${projectId}/${pid}/upload-image`, fd);
      }
      if (submittals.isDirty) {
        await commitSubmittals(submittals, pid, projectId, 'flashing-sheet-metal-products', axios, API);
      }
      if (!isEdit) {
        if (draftId) clearWizardState(companyId, draftId);
        clearWizardState(companyId, 'new');
      }
      toast.success(isEdit ? 'Product updated' : 'Product created');
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally { setFinalizing(false); }
  };

  /* ── Manufacturer CRUD ───────────────────────────────── */
  const handleCreateManufacturer = async () => {
    const name = mfrSearch.trim(); if (!name) return;
    setCreatingMfr(true);
    try {
      const res = await axios.post(`${API}/thermal-insulation-manufacturers`, { name });
      setManufacturers(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(prev => ({ ...prev, manufacturerId: res.data.id }));
      setMfrSearch(''); setMfrDropdownOpen(false);
      toast.success(`Manufacturer "${name}" created`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create manufacturer'); }
    finally { setCreatingMfr(false); }
  };
  const handleSelectManufacturer = (m) => { setForm(prev => ({ ...prev, manufacturerId: m.id })); setMfrSearch(''); setMfrDropdownOpen(false); };
  const handleSelectCountry = (c) => { setForm(prev => ({ ...prev, countryCode: c.code })); setCountrySearch(''); setCountryDropdownOpen(false); };

  /* ── Image handlers ──────────────────────────────────── */
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    updateField('imageFile', file);
    updateField('imagePreview', URL.createObjectURL(file));
  };
  const handlePasteUpload = async (f) => {
    setPasteUploading(true);
    try { updateField('imageFile', f); updateField('imagePreview', URL.createObjectURL(f)); setShowPasteModal(false); }
    finally { setPasteUploading(false); }
  };

  /* ── Submittal preview ───────────────────────────────── */
  const handlePreviewSubmittal = async (sub) => {
    if (!draftId || sub._status === 'pending') return;
    try {
      const res = await axios.get(`${API}/flashing-sheet-metal-products/${projectId}/${draftId}/submittals/${sub.id}/preview-url`);
      setPreviewSubmittal({ ...sub, cloudinaryUrl: res.data?.url });
    } catch { toast.error('Failed to load preview'); }
  };

  /* ══════════════════════════════════════════════════════ */
  /* ── RENDER ──────────────────────────────────────────── */
  /* ══════════════════════════════════════════════════════ */
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-4xl p-0 flex flex-col" style={{ height: '75vh', maxHeight: '85vh', minHeight: '400px' }} data-testid="flashing-wizard">
          {/* Header */}
          <div className="px-6 pt-5 pb-3 border-b shrink-0">
            <DialogHeader>
              <DialogTitle data-testid="wizard-title">{isEdit ? 'Edit Product' : 'Add Flashing & Sheet Metal Product'}</DialogTitle>
              <DialogDescription>Step {step} of {TOTAL_STEPS}: {WIZARD_STEPS[step - 1]?.title}</DialogDescription>
            </DialogHeader>
            <WizardStepper steps={WIZARD_STEPS} currentStep={step} onStepClick={setStep} />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">

            {/* ── STEP 1: Product Identification ────────── */}
            {step === 1 && (
              <div className="space-y-4" data-testid="fsm-wizard-step-1">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Product Identification</h4>
                <input ref={imageFileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleImageSelect} data-testid="image-file-input" />
                <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 items-stretch" data-testid="product-info-grid">
                  {/* Image upload area */}
                  <div className="border-2 border-dashed rounded-lg flex flex-col items-center justify-center relative overflow-hidden bg-slate-50 transition-colors min-h-[220px] border-slate-300" data-testid="image-upload-area">
                    {displayImage ? (
                      <>
                        <img src={displayImage} alt="Preview" className="w-full h-full object-contain p-2" data-testid="image-preview" />
                        <button type="button" className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 z-10" onClick={() => { updateField('imageFile', null); updateField('imagePreview', null); }} data-testid="remove-image-btn"><X className="h-3 w-3" /></button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 p-4 text-center"><ImageIcon className="h-10 w-10 text-slate-300" /><p className="text-xs text-slate-400">Product Image</p><p className="text-[10px] text-slate-400">PNG, JPG, WebP (max 5MB)</p></div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-white/90 border-t border-slate-200 flex items-center justify-center gap-1.5 py-1.5 px-2">
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => setShowPasteModal(true)} data-testid="paste-from-clipboard-btn"><ClipboardPaste className="h-3.5 w-3.5 mr-1" />Paste</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => imageFileRef.current?.click()} data-testid="upload-image-btn"><Upload className="h-3.5 w-3.5 mr-1" />{displayImage ? 'Change' : 'Upload'}</Button>
                    </div>
                  </div>

                  {/* Form fields */}
                  <div className="space-y-3">
                    {/* Manufacturer (required) */}
                    <div className="space-y-1">
                      <Label className="text-sm">Manufacturer *</Label>
                      <div className="relative" ref={mfrRef}>
                        <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${errors.manufacturerId ? 'border-red-500' : 'border-input'}`} onClick={() => setMfrDropdownOpen(true)} data-testid="manufacturer-select">
                          {selectedManufacturer ? (
                            <div className="flex items-center justify-between w-full"><span className="text-sm">{selectedManufacturer.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, manufacturerId: '' })); }} /></div>
                          ) : (<span className="text-muted-foreground text-sm">Select manufacturer...</span>)}
                        </div>
                        {errors.manufacturerId && <p className="text-xs text-red-500">{errors.manufacturerId}</p>}
                        {mfrDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                            <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search or add new..." value={mfrSearch} onChange={(e) => setMfrSearch(e.target.value)} autoFocus data-testid="manufacturer-search-input" /></div></div>
                            <div className="py-1">
                              {filteredManufacturers.map((m) => (
                                <div key={m.id} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${m.id === form.manufacturerId ? 'bg-slate-50 font-medium' : ''}`} onClick={() => handleSelectManufacturer(m)} data-testid={`manufacturer-option-${m.id}`}>
                                  {m.id === form.manufacturerId && <Check className="h-4 w-4 mr-2 text-orange-500" />}
                                  <span className={m.id === form.manufacturerId ? '' : 'ml-6'}>{m.name}</span>
                                  {m.isMine && <UserCheck className="h-3.5 w-3.5 ml-auto text-orange-400 shrink-0" title="Added by you" />}
                                </div>
                              ))}
                              {showAddNew && (
                                <div className="flex items-center px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-orange-600 font-medium border-t" onClick={handleCreateManufacturer} data-testid="add-new-manufacturer-btn">
                                  {creatingMfr ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add new: &quot;{mfrSearch.trim()}&quot;
                                </div>
                              )}
                              {filteredManufacturers.length === 0 && !showAddNew && (<div className="px-3 py-2 text-sm text-slate-400">No manufacturers found</div>)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Product Name / Model */}
                    <FormField label="Product Name / Model" required error={errors.productNameModel}>
                      <Input placeholder="e.g., Firestone Metal Products" value={form.productNameModel} onChange={(e) => updateField('productNameModel', e.target.value)} className={errors.productNameModel ? 'border-red-500' : ''} data-testid="product-name-input" />
                    </FormField>

                    {/* Country of Manufacturer */}
                    <div className="space-y-1">
                      <Label className="text-sm">Country of Manufacturer</Label>
                      <div className="relative" ref={countryRef}>
                        <div className={`flex items-center border rounded-md px-3 py-2 cursor-pointer ${errors.countryCode ? 'border-red-500' : 'border-input'}`} onClick={() => setCountryDropdownOpen(true)} data-testid="country-select">
                          {selectedCountry ? (<div className="flex items-center justify-between w-full"><span className="text-sm">{selectedCountry.name}</span><X className="h-4 w-4 text-slate-400 hover:text-slate-600" onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, countryCode: '' })); }} /></div>) : (<span className="text-muted-foreground text-sm">Select country...</span>)}
                        </div>
                        {countryDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-52 overflow-auto">
                            <div className="sticky top-0 bg-white p-2 border-b"><div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input className="pl-8" placeholder="Search country..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} autoFocus data-testid="country-search-input" /></div></div>
                            <div className="py-1">{filteredCountries.slice(0, 50).map((c) => (<div key={c.code} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${c.code === form.countryCode ? 'bg-slate-50 font-medium' : ''}`} onClick={() => handleSelectCountry(c)} data-testid={`country-option-${c.code}`}>{c.code === form.countryCode && <Check className="h-4 w-4 mr-2 text-orange-500" />}<span className={c.code === form.countryCode ? '' : 'ml-6'}>{c.name}</span></div>))}</div>
                          </div>
                        )}
                      </div>
                      {errors.countryCode && <p className="text-xs text-red-500">{errors.countryCode}</p>}
                    </div>
                  </div>
                </div>

                {/* Product Role + Application Area */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Product Role" required error={errors.productRole}>
                    <MultiSelectCheckboxes options={PRODUCT_ROLES} selected={form.productRole || []} onChange={(v) => updateField('productRole', v)} testIdPrefix="product-role" />
                  </FormField>
                  <FormField label="Application Area" required error={errors.applicationArea}>
                    <MultiSelectCheckboxes options={APPLICATION_AREAS} selected={form.applicationArea || []} onChange={(v) => updateField('applicationArea', v)} testIdPrefix="app-area" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ── STEP 2: Material & Fabrication Properties ─ */}
            {step === 2 && (
              <div className="space-y-4" data-testid="fsm-wizard-step-2">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Material & Fabrication Properties</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Metal Type" required error={errors.metalType}>
                    <FormSelect value={form.metalType} onValueChange={(v) => updateField('metalType', v)} placeholder="Select metal type..." options={METAL_TYPES} testId="metal-type-select" error={!!errors.metalType} />
                  </FormField>
                  <FormField label="Gauge / Thickness">
                    <Input type="text" inputMode="decimal" placeholder="e.g., 24 ga or 1/16" value={form.gaugeThickness} onChange={(e) => updateField('gaugeThickness', sanitizeFraction(e.target.value))} data-testid="gauge-thickness-input" />
                  </FormField>
                  <FormField label="Finish / Coating">
                    <Input placeholder="e.g., Kynar/PVDF coating" value={form.finishCoating} onChange={(e) => updateField('finishCoating', e.target.value)} data-testid="finish-coating-input" />
                  </FormField>
                  <FormField label="Fabrication Method">
                    <FormSelect value={form.fabricationMethod} onValueChange={(v) => updateField('fabricationMethod', v)} placeholder="Select method..." options={FABRICATION_METHODS} testId="fabrication-method-select" />
                    {form.fabricationMethod === 'Field modified' && (
                      <div className="flex items-center gap-2 text-xs rounded-md px-2.5 py-1.5 mt-1.5 text-amber-600 bg-amber-50 border border-amber-200" data-testid="field-modified-warning">
                        <Info className="h-3.5 w-3.5 shrink-0" />
                        <span>Field modification may require additional site inspection.</span>
                      </div>
                    )}
                  </FormField>
                </div>
              </div>
            )}

            {/* ── STEP 3: Fire, Code & Assembly Compliance ── */}
            {step === 3 && (
              <div className="space-y-4" data-testid="fsm-wizard-step-3">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Fire, Code & Assembly Compliance</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="NFPA 285 Compatibility">
                    <FormSelect value={form.fireCodeCompliance.nfpa285Compatibility} onValueChange={(v) => updateNested('fireCodeCompliance', 'nfpa285Compatibility', v)} placeholder="Select..." options={NFPA_OPTIONS} testId="nfpa285-select" />
                  </FormField>
                  <FormField label="ASTM E84">
                    <Input placeholder="e.g., Class A" value={form.fireCodeCompliance.astmE84} onChange={(e) => updateNested('fireCodeCompliance', 'astmE84', e.target.value)} data-testid="astm-e84-input" />
                  </FormField>
                  <FormField label="Assembly Reference">
                    <Input placeholder="e.g., UL Design No. W-L-0048" value={form.fireCodeCompliance.assemblyReference} onChange={(e) => updateNested('fireCodeCompliance', 'assemblyReference', e.target.value)} data-testid="assembly-reference-input" />
                  </FormField>
                  <FormField label="Local Code Notes">
                    <Input placeholder="e.g., Compliant with IBC 2021 Section 1404" value={form.fireCodeCompliance.localCodeNotes} onChange={(e) => updateNested('fireCodeCompliance', 'localCodeNotes', e.target.value)} data-testid="local-code-notes-input" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ── STEP 4: Warranty & Responsibility ──────── */}
            {step === 4 && (
              <div className="space-y-4" data-testid="fsm-wizard-step-4">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Warranty & Responsibility</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Manufacturer Material Warranty (years)" error={errors['warranty.manufacturerWarrantyYears']}>
                    <Input type="text" inputMode="decimal" placeholder="e.g., 20" value={form.warranty.manufacturerWarrantyYears} onChange={(e) => updateNested('warranty', 'manufacturerWarrantyYears', sanitizeNumeric(e.target.value))} className={errors['warranty.manufacturerWarrantyYears'] ? 'border-red-500' : ''} data-testid="mfr-warranty-input" />
                  </FormField>
                  <FormField label="Installer Workmanship Warranty (years)" error={errors['warranty.installerWarrantyYears']}>
                    <Input type="text" inputMode="decimal" placeholder="e.g., 5" value={form.warranty.installerWarrantyYears} onChange={(e) => updateNested('warranty', 'installerWarrantyYears', sanitizeNumeric(e.target.value))} className={errors['warranty.installerWarrantyYears'] ? 'border-red-500' : ''} data-testid="installer-warranty-input" />
                  </FormField>
                  <FormField label="Scope Exclusions">
                    <Input placeholder="e.g., UV degradation excluded" value={form.warranty.scopeExclusions} onChange={(e) => updateNested('warranty', 'scopeExclusions', e.target.value)} data-testid="scope-exclusions-input" />
                  </FormField>
                  <FormField label="Maintenance Responsibility">
                    <Input placeholder="e.g., Annual inspection required" value={form.warranty.maintenanceResponsibility} onChange={(e) => updateNested('warranty', 'maintenanceResponsibility', e.target.value)} data-testid="maintenance-responsibility-input" />
                  </FormField>
                </div>
              </div>
            )}

            {/* ── STEP 5: Submittals ───────────────────── */}
            {step === 5 && (
              <WizardSubmittalsStep
                displayList={submittals.displayList}
                onAddUpload={submittals.addUpload}
                onRemoveItem={submittals.removeItem}
                onUndoDelete={submittals.undoDelete}
                onPreview={handlePreviewSubmittal}
                docTypes={SUBMITTAL_DOC_TYPES}
              />
            )}

            {/* ── STEP 6: Visual Coverage ──────────────── */}
            {step === 6 && (
              <div className="space-y-4" data-testid="fsm-wizard-step-6">
                <h4 className="font-semibold text-sm border-b pb-2 mb-3">Visual Coverage <span className="text-xs text-slate-400 font-normal">(Optional)</span></h4>
                <p className="text-xs text-slate-500 italic">Illustrative only — not part of specification</p>

                {/* Marker Type */}
                <FormField label="Marker Type">
                  <div className="flex flex-wrap gap-2">
                    {FSM_MARKER_TYPES.map(mt => {
                      const meta = FSM_MARKER_META[mt];
                      const selected = form.visualCoverage.markerType === mt;
                      const count = (form.visualCoverage.markers || []).filter(m => m.type === mt).length;
                      return (
                        <Badge key={mt} variant={selected ? 'default' : 'outline'}
                          className={`cursor-pointer transition-colors ${selected ? meta.hoverBg : 'hover:bg-slate-100'}`}
                          onClick={() => setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, markerType: selected ? '' : mt } }))}
                          data-testid={`fsm-marker-${mt.toLowerCase()}`}>
                          <span className={`inline-block w-2.5 h-2.5 mr-1.5 ${meta.shape}`}
                            style={{ backgroundColor: selected ? '#fff' : meta.color }} />
                          {mt}{count > 0 ? ` (${count})` : ''}
                        </Badge>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{form.visualCoverage.markerType ? `Click on the elevation image below to place "${form.visualCoverage.markerType}" marker` : 'Select a marker type, then click on the elevation image to place'}</p>
                </FormField>

                {/* Elevation Upload */}
                <FormField label="Elevation Upload">
                  {elevationUploading && (
                    <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-2">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Processing file...</p>
                        <p className="text-xs text-blue-600">Converting elevation drawing to image</p>
                      </div>
                    </div>
                  )}
                  {form.visualCoverage.elevationUrl ? (
                    <div className="border rounded-lg overflow-hidden bg-slate-50">
                      {/* Filename bar */}
                      <div className="flex items-center justify-between px-3 py-2 bg-white border-b">
                        <div className="flex items-center gap-2 text-sm min-w-0">
                          <FileText className="h-4 w-4 text-red-500 shrink-0" />
                          <span className="truncate font-medium" data-testid="fsm-elevation-filename">{form.visualCoverage.elevationFileName || 'elevation'}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0 hover:bg-red-100 hover:text-red-600"
                          onClick={() => { setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, elevationFileName: '', elevationUrl: '', elevationPublicId: '', markers: [] } })); setElevationZoom(1); }}
                          data-testid="fsm-remove-elevation"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                      {/* Rendered elevation image with marker placement */}
                      <div className="relative overflow-auto" style={{ maxHeight: '420px' }} data-testid="fsm-elevation-container">
                        <div className="sticky top-1 right-1 z-20 flex justify-end px-2 py-1">
                          <ZoomControls zoom={elevationZoom}
                            onZoomIn={() => setElevationZoom(z => Math.min(z + 0.25, 3))}
                            onZoomOut={() => setElevationZoom(z => Math.max(z - 0.25, 0.5))}
                            onReset={() => setElevationZoom(1)} />
                        </div>
                        <div className="relative" style={{ transform: `scale(${elevationZoom})`, transformOrigin: 'top left', width: '100%' }}>
                          <img src={form.visualCoverage.elevationUrl} alt="Elevation" className="w-full rounded"
                            style={{ cursor: form.visualCoverage.markerType ? 'crosshair' : 'default', display: 'block' }}
                            onClick={(e) => {
                              if (!form.visualCoverage.markerType) return;
                              const rect = e.target.getBoundingClientRect();
                              const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
                              const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);
                              setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, markers: [...(prev.visualCoverage.markers || []), { type: prev.visualCoverage.markerType, x: parseFloat(x), y: parseFloat(y) }] } }));
                            }} data-testid="fsm-elevation-image" />
                          {(form.visualCoverage.markers || []).map((m, i) => {
                            const meta = FSM_MARKER_META[m.type] || {};
                            return (
                              <div key={i}
                                className={`absolute border-2 border-white shadow-lg cursor-pointer ${meta.shape === 'rounded-sm' ? 'w-3.5 h-3.5 rounded-sm' : 'w-3 h-3 rounded-full'}`}
                                style={{ left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%, -50%)', backgroundColor: meta.color ? `${meta.color}d9` : 'rgba(100,100,100,0.85)' }}
                                title={`${m.type} — click to remove`}
                                onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, markers: prev.visualCoverage.markers.filter((_, j) => j !== i) } })); }}
                                data-testid={`fsm-marker-${i}`} />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : !elevationUploading && (
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <label className="cursor-pointer text-slate-400 hover:text-orange-500 transition-colors">
                        <Upload className="h-7 w-7 mx-auto mb-1.5" />
                        <span className="text-sm font-medium">Upload Drawing</span>
                        <p className="text-xs text-slate-400 mt-0.5">PDF, JPG, or PNG files</p>
                        <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" data-testid="fsm-elevation-upload" onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const isPdf = f.type === 'application/pdf';
                          const isImage = ['image/jpeg', 'image/png'].includes(f.type);
                          if (!isPdf && !isImage) { toast.error('Only PDF, JPG, and PNG files are allowed'); return; }
                          setElevationUploading(true);
                          try {
                            const fd = new FormData();
                            fd.append('file', f);
                            if (isPdf) {
                              const res = await axios.post(`${API}/projects/${projectId}/elevations/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              const firstPage = res.data?.pages?.[0];
                              if (firstPage) {
                                setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, elevationFileName: f.name, elevationUrl: firstPage.fullImageUrl || firstPage.previewUrl, elevationPublicId: firstPage.publicId } }));
                                toast.success('Elevation uploaded');
                              }
                            } else {
                              fd.append('type', 'flashing');
                              const res = await axios.post(`${API}/projects/${projectId}/elevations/upload-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              const elev = res.data?.elevation;
                              const imgUrl = elev?.imageUrl || elev?.fullImageUrl || elev?.previewUrl;
                              if (imgUrl) {
                                setForm(prev => ({ ...prev, visualCoverage: { ...prev.visualCoverage, elevationFileName: f.name, elevationUrl: imgUrl, elevationPublicId: elev?.publicId || '' } }));
                                toast.success('Elevation uploaded');
                              }
                            }
                          } catch { toast.error('Upload failed'); }
                          finally { setElevationUploading(false); e.target.value = ''; }
                        }} />
                      </label>
                    </div>
                  )}
                </FormField>
              </div>
            )}

            {/* ── STEP 7: Review Summary ───────────────── */}
            {step === 7 && (
              <div className="space-y-0 divide-y divide-slate-200 bg-slate-50 rounded-lg p-4" data-testid="fsm-wizard-step-7">
                <ReviewProductHeader
                  imagePreview={form.imagePreview || existingImageUrl}
                  qrToken={draftId ? (editProduct?.qrToken || draftId) : null}
                  infoRows={[
                    ['Product Name', form.productNameModel],
                    ['Manufacturer', selectedManufacturer?.name],
                    ['Country of Manufacturer', selectedCountry?.name],
                  ]}
                  hideInfoSection
                />
                <div className="pt-3">
                  <ReviewSection title="Product Identification" rows={[
                    ['Manufacturer', selectedManufacturer?.name],
                    ['Country of Manufacturer', selectedCountry?.name],
                    ['Product Name / Model', form.productNameModel],
                    ['Product Role', form.productRole?.join(', ')],
                    ['Application Area', form.applicationArea?.join(', ')],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Material & Fabrication Properties" rows={[
                    ['Metal Type', form.metalType],
                    ['Gauge / Thickness', form.gaugeThickness],
                    ['Finish / Coating', form.finishCoating],
                    ['Fabrication Method', form.fabricationMethod],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Fire, Code & Assembly Compliance" rows={[
                    ['NFPA 285 Compatibility', badge(form.fireCodeCompliance.nfpa285Compatibility)],
                    ['ASTM E84', form.fireCodeCompliance.astmE84],
                    ['Assembly Reference', form.fireCodeCompliance.assemblyReference],
                    ['Local Code Notes', form.fireCodeCompliance.localCodeNotes],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Warranty & Responsibility" rows={[
                    ['Manufacturer Material Warranty', form.warranty.manufacturerWarrantyYears ? `${form.warranty.manufacturerWarrantyYears} years` : null],
                    ['Installer Workmanship Warranty', form.warranty.installerWarrantyYears ? `${form.warranty.installerWarrantyYears} years` : null],
                    ['Scope Exclusions', form.warranty.scopeExclusions],
                    ['Maintenance Responsibility', form.warranty.maintenanceResponsibility],
                  ]} />
                </div>
                <WizardSubmittalsReview displayList={submittals.displayList} docTypes={SUBMITTAL_DOC_TYPES} />
                <div className="pt-3">
                  <ReviewSection title="Visual Coverage" rows={[
                    ['Elevation PDF', form.visualCoverage.elevationFileName || '—'],
                    ['Markers', form.visualCoverage.markers?.length ? `${form.visualCoverage.markers.length} marker(s)` : '—'],
                  ]} />
                </div>
                <div className="pt-3">
                  <ReviewSection title="Status" rows={[['Status', 'Active']]} />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleClose} data-testid="cancel-btn">Cancel</Button>
              <Button variant="outline" onClick={handleSaveDraft} disabled={saving || finalizing || !isDirty} data-testid="save-draft-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save as Draft
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 1} data-testid="back-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
              {step < TOTAL_STEPS ? (
                <Button onClick={() => setStep(s => s + 1)} disabled={saving} data-testid="next-btn">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button onClick={handleFinalize} disabled={finalizing || saving} data-testid="finalize-btn">
                  {finalizing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{isEdit ? 'Save' : 'Create Product'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmState} onClose={() => setConfirmState({ open: false })} />
      <SubmittalPreviewModal open={!!previewSubmittal} onClose={() => setPreviewSubmittal(null)} submittal={previewSubmittal} />
      <ClipboardPasteModal isOpen={showPasteModal} onClose={() => setShowPasteModal(false)} onUpload={handlePasteUpload} title="Paste Product Image" uploading={pasteUploading} />
    </>
  );
};

export default AddFlashingWizard;
